import React from 'react';
import Button from '@material-ui/core/Button';
import Dialog, { type DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import { useForm } from 'react-hook-form';
import { TextInput } from '../generic';

interface EnterVerificationCodeDialogProps extends Omit<DialogProps, 'open' | 'onSubmit'> {
  isOpen: boolean;
  onBack: () => void;
  onSubmit: (data: VerificationCode) => void;
  resendCode: () => void;
  secsUntilResend: number;
  sendCodeError: string | null;
}

export type VerificationCode = { verificationCode: string };

export function EnterVerificationCodeDialog(props: EnterVerificationCodeDialogProps) {
  const { isOpen, onSubmit, onBack, resendCode, secsUntilResend, sendCodeError, ...rest } = props;

  const form = useForm<VerificationCode>();

  React.useEffect(() => {
    if (sendCodeError) {
      form.setError('verificationCode', { message: sendCodeError ?? undefined });
    }
    // 👇 Update on submitCount so that if the code is wrong again the
    // message is shown again even if the message was never cleared
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendCodeError, form.formState.submitCount]);

  return (
    <Dialog {...rest} open={isOpen}>
      <form className="px-4 py-8 sm:px-10 sm:pt-11" onSubmit={form.handleSubmit(onSubmit)}>
        <h2 className="text-gray-900 text-3xl m-0">Enter Verification Code</h2>

        <div className="mt-4 mb-8 flex flex-col gap-4 text-grey-900">
          <div>A verification code has been sent. Please enter the code below:</div>

          <TextInput
            label="6-Digit Code"
            mask="999999"
            maskChar={null}
            className="mt-2"
            autoComplete="tel"
            inputProps={form.register('verificationCode', {
              required: 'Required',
              pattern: { value: /^[0-9]{6}$/, message: 'Code must be 6 digits' },
            })}
            error={form.formState.errors.verificationCode?.message}
          />

          <p>
            <b>Didn't get it?</b>
            &nbsp;
            {secsUntilResend ? (
              <span>
                Request a new code in <b className="font-bold">{secsUntilResend}</b> seconds.
              </span>
            ) : (
              <button
                className="reset-button-styles text-blue-800 hover:underline hover:text-blue-600"
                onClick={resendCode}
              >
                Receive a new code.
              </button>
            )}
          </p>
        </div>

        <DialogActions>
          <Button type="button" color="primary" autoFocus variant="text" size="medium" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" color="primary" autoFocus variant="contained" size="medium">
            Continue
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
